import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { UIInput, UISelect, UICustomSelect } from "components/Addtaxcloudmodal";
import AddressAutocomplete from "components/AddressAutocomplete";
import "bootstrap-icons/font/bootstrap-icons.css";
import { FaExclamationTriangle } from "react-icons/fa";
import taxcloudlogo from "assets/images/brands/taxcloudlogo.png";
import stripelogo from "assets/images/brands/stripelogo.png";
import upslogo from "assets/images/brands/ups.png";
import "./SignageIntegrations.css";
import { color } from "assets/libs/chart.js/helpers";
import { Link, useLocation } from "react-router-dom";
import { getAuthCookie } from "../../utils/authCookies";
/** ========= Types ========= */
type Category = "All" | "Payment" | "Accounting" | "Marketing" | "Documents" | "Automation" | "CRM" | "Communication" | "Shipping" | string;

type Card = {
  id: "stripe" | "taxcloud" | "ups" | "muted";
  key: string;
  name: string;
  category: Exclude<Category, "All">;
  connected: boolean;
  configurable: boolean;
  muted?: boolean;
  icon?: React.ReactNode;
};

type StripeGateway = {
  id: string;
  name: string;
  publishable_key: string;
  secret_key_masked?: string;
  status: "Active" | "Inactive"; // UI string; API expects boolean
};

type TaxCloudConfig = {
  id: string;
  connection_id: string;
  api_key: string;
  origin_address: {
    label: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    country: string;
    zip: string;
    phone?: string;
    cityCode?: string;
    stateCode?: string;
    countryCode?: string;
    latitude?: number;
    longitude?: number;
  };
  tic_codes: {
    default: string,
    shipping: string,
    rush: string,
    service: string
  },
  version?: string;
  status: "Active" | "Inactive"; // UI string; API expects boolean
};

// --- UPS types ---
type UpsConfig = {
  id: string;
  integration_type: "shipping";
  provider: "ups";
  auth_mode: string;
  client_id?: string;
  client_secret?: string;
  account_number?: string;
  environment?: "production" | "sandbox" | string;
  status?: boolean;            // <-- use this to decide "connected"
  created_at?: string;
  updated_at?: string;
  created_by?: { id: string; name: string };
  updated_by?: { id: string; name: string };
};

type Mode = "add" | "edit";

/** ========= Component ========= */
export default function IntegrationsPage({ API = process.env.REACT_APP_API_URL }: { API?: string }) {
  const token = getAuthCookie("sessionToken") || "";
  const location = useLocation();
  const baseRoute = location.pathname.includes("/signageteammember") ? "/signageteammember" : "/signage";

  /** --------- page filters --------- */
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<Category>("All");
  const [connectedOnly, setConnectedOnly] = useState(false);

  /** --------- live connections --------- */
  const [stripe, setStripe] = useState<StripeGateway | null>(null);
  const [taxcloud, setTaxcloud] = useState<TaxCloudConfig | null>(null);
  const [ups, setUps] = useState<UpsConfig | null>(null);
  const [loading, setLoading] = useState(true);

  /** --------- modals & modes --------- */
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [stripeMode, setStripeMode] = useState<Mode>("add");
  const [taxMode, setTaxMode] = useState<Mode>("add");

  /** --------- nested danger --------- */
  const [danger, setDanger] = useState<{ for: "stripe" | "taxcloud" } | null>(null);

  const showError = (m: string) => console.error(m);
  const showSuccess = (m: string) => console.log(m);

  /** ================= Fetch connections ================= */
  const fetchIntegrations = async () => {
    try {
      setLoading(true);

      // STRIPE list
      const resp = await fetch(`${API}/payments/gateway`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!resp.ok) throw new Error("Failed to fetch integrations.");
      const methods = await resp.json();
      const stripeItem = Array.isArray(methods)
        ? methods.find((m: any) => (m?.gateway || m?.provider || m?.name || "").toLowerCase() === "stripe")
        : null;
      setStripe(
        stripeItem
          ? {
            id: String(stripeItem.id || stripeItem._id),
            name: stripeItem.name || "Stripe Production",
            publishable_key: stripeItem.publishable_key || stripeItem.pk || "",
            secret_key_masked: stripeItem.secret_key_masked || "********************************",
            status: (stripeItem.status ? "Active" : "Inactive") as "Active" | "Inactive",
          }
          : null
      );

      // TAXCLOUD list
      const tRes = await fetch(`${API}/taxcloud/config`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (tRes.ok) {
        const t = await tRes.json();
        const first = Array.isArray(t) ? t[0] : t;
        setTaxcloud(
          first
            ? {
              id: String(first.id || first._id),
              connection_id: first.connection_id || "",
              api_key: first.api_key ? "***************" : "",
              origin_address: {
                label: first.origin_address?.label || "",
                addressLine1: first.origin_address?.addressLine1 || "",
                addressLine2: first.origin_address?.addressLine2 || "",
                city: first.origin_address?.city || "",
                state: first.origin_address?.state || "",
                country: first.origin_address?.country || "",
                zip: first.origin_address?.zip || "",
                phone: first.origin_address?.phone || "",
                cityCode: first.origin_address?.cityCode || "",
                stateCode: first.origin_address?.stateCode || "",
                countryCode: first.origin_address?.countryCode || "",
                latitude: first.origin_address?.latitude,
                longitude: first.origin_address?.longitude,
              },
              tic_codes: {
                default: first?.tic_codes?.default || "",
                shipping: first?.tic_codes?.shipping || "",
                rush: first?.tic_codes?.rush || "",
                service: first?.tic_codes?.service || ""
              },

              version: first.version || "v3",
              status: (first.status ? "Active" : "Inactive") as "Active" | "Inactive",
            }
            : null
        );
      } else {
        setTaxcloud(null);
      }

      // UPS list (take 0th record)
      try {
        const uRes = await fetch(`${API}/shipping/ups/configs`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });

        if (uRes.ok) {
          const raw = await uRes.json();
          const list: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
          const first = list[0];

          if (first) {
            // normalize id (_id fallback)
            const normalized: UpsConfig = {
              ...first,
              id: String(first.id || first._id),
            };
            setUps(normalized);
            console.log("UPS config:", normalized);
          } else {
            setUps(null);
          }
        } else {
          setUps(null);
        }
      } catch {
        setUps(null);
      }
    } catch (e) {
      showError("Failed to load integrations.");
    } finally {
      setLoading(false);
    }
  };
  const upsConnected = Boolean(ups?.status === true);


  useEffect(() => {
    fetchIntegrations();
    // eslint-disable-next-line
  }, [token]);

  /** ================= Cards ================= */
const CARDS: Card[] = useMemo(() => {
  const base: Card[] = [
    {
      id: "stripe",
      key: "stripe",
      name: "Stripe",
      category: "Payment",
      connected: !!stripe,
      configurable: !!stripe,
      icon: <img src={stripelogo} alt="Stripe" className="int-icon-img" />,
    },
    {
      id: "taxcloud",
      key: "taxcloud",
      name: "TaxCloud",
      category: "Accounting",
      connected: !!taxcloud,
      configurable: !!taxcloud,
      icon: <img src={taxcloudlogo} alt="TaxCloud" className="int-icon-img" />,
    },
    {
      id: "ups",
      key: "ups",
      name: "UPS",
      category: "Shipping",
      connected: upsConnected,     // ✅ status-based
      configurable: upsConnected,  // ✅ status-based
      icon: <img src={upslogo} alt="UPS" className="int-icon-img" />,
    },
  ];

  let rows = base;
  if (category !== "All") rows = rows.filter(r => r.category === category);
  if (connectedOnly) rows = rows.filter(r => r.connected);
  if (query.trim()) {
    const q = query.toLowerCase();
    rows = rows.filter(r => r.name.toLowerCase().includes(q));
  }
  return rows;
// ✅ depend on upsConnected (not raw ups object)
}, [stripe, taxcloud, upsConnected, category, connectedOnly, query]);

  /** ================= Disconnect ================= */
  const disconnectStripe = async () => {
    try {
      if (!stripe?.id) return;
      await fetch(`${API}/payments/gateway/${stripe.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      showSuccess("Stripe disconnected");
    } catch (e) {
      showError("Failed to disconnect Stripe");
    } finally {
      setDanger(null);
      setShowStripeModal(false);
      await fetchIntegrations();
    }
  };

  const disconnectTaxcloud = async () => {
    try {
      if (!taxcloud?.id) return;
      await fetch(`${API}/taxcloud/config/${taxcloud.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      showSuccess("TaxCloud disconnected");
    } catch (e) {
      showError("Failed to disconnect TaxCloud");
    } finally {
      setDanger(null);
      setShowTaxModal(false);
      await fetchIntegrations();
    }
  };

  /** ================= Stripe (Formik) ================= */
  const stripeSchema = yup.object({
    name: yup.string().required("Name is required"),
    publishable_key: yup.string().required("Publishable key is required"),
    // secret required when adding; optional on edit
    // secret_key: yup.string().when("$mode", (mode: Mode, s) => (mode === "add" ? s.required("Secret key is required") : s.optional())),
    statuss: yup.string().oneOf(["Active", "Inactive"]).required(),
  });

  const stripeForm = useFormik({
    initialValues: { name: "", publishable_key: "", secret_key: "", statuss: "Active" as "Active" | "Inactive" },
    validationSchema: stripeSchema,
    validateOnBlur: true,
    validateOnChange: false,
    // context: { mode: stripeMode },
    onSubmit: async (values, helpers) => {
      try {
        const isAdd = stripeMode === "add" || !stripe?.id;
        const url = isAdd ? `${API}/payments/gateway` : `${API}/payments/gateway/${stripe?.id}`;
        const method = isAdd ? "POST" : "PUT";
        const body = {
          name: values.name || "",
          gateway: "stripe",
          api_key: "",
          secret_key: values.secret_key || "",
          publishable_key: values.publishable_key || "",
          status: values.statuss === "Active" ? true : false,
        };
        const response = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        if (!response.ok) throw new Error("Failed to save data");
        await response.json().catch(() => ({}));
        showSuccess(isAdd ? "Stripe connected!" : "Stripe updated!");
        setShowStripeModal(false);
        await fetchIntegrations();
      } catch (err: any) {
        showError(err?.message || "Failed to save Stripe.");
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const openStripeModal = async () => {
    if (loading) return; // Prevent opening modal during loading
    if (stripe?.id) {
      setStripeMode("edit");
      try {
        const r = await fetch(`${API}/payments/gateway/${stripe.id}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const d = r.ok ? await r.json() : stripe;
        stripeForm.setValues({
          name: d?.name || "",
          publishable_key: d?.publishable_key || "",
          secret_key: d?.secret_key || "", // keep empty unless changed
          statuss: (d?.status === true || d?.status === "Active") ? "Active" : "Inactive",
        });
      } catch {
        stripeForm.setValues({
          name: stripe.name || "",
          publishable_key: stripe.publishable_key || "",
          secret_key: "",
          statuss: stripe.status || "",
        });
      }
    } else {
      setStripeMode("add");
      stripeForm.resetForm();
      stripeForm.setValues({ name: "", publishable_key: "", secret_key: "", statuss: "Active" });
    }
    setShowStripeModal(true);
  };

  /** ================= TaxCloud (Formik) ================= */
  // requested validations mapped to origin_address.*
  const addressReq = yup.object({
    label: yup.string().required("Label is required"),
    // phone: yup.string().required("Phone is required"),
    addressLine1: yup.string().required("Address line 1 is required"),
    city: yup.string().required("City is required"),
    state: yup.string().required("State is required"),
    zip: yup.string().required("Zip Code is required"),
    country: yup.string().required("Country is required"),
    addressLine2: yup.string().optional(),
    cityCode: yup.string().optional(),
    stateCode: yup.string().optional(),
    countryCode: yup.string().optional(),
    latitude: yup.number().optional(),
    longitude: yup.number().optional(),
  });


  const ticCodesReq = yup.object({
    default: yup.string().required("Default TIC code is required"),
    shipping: yup.string().required("Shipping TIC code is required"),
    rush: yup.string().required("Special Fee TIC code is required"),
    service: yup.string().required("Service TIC code is required"),
  });

  const taxSchema = yup.object({
    connection_id: yup.string().required("Connection ID is required"),
    api_key: yup.string().required("API key is required"),
    origin_address: addressReq,
    version: yup.string().required("Version is required"),
    statuss: yup.string().oneOf(["Active", "Inactive"]).required(),
  });

  const taxForm = useFormik({
    initialValues: {
      // provider: "taxcloud",
      // account_number: "",
      // license_key: "",
      connection_id: "",
      // client_id: "qbo-client-id",
      // client_secret: "qbo-client-secret",
      // environment: "prodcution",
      api_key: "",
      origin_address: {
        label: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        country: "",
        zip: "",
        cityCode: "",
        stateCode: "",
        countryCode: "",
        // latitude: undefined as number | undefined,
        // longitude: undefined as number | undefined,
        phone: "",
      }, tic_codes: {
        default: "",
        shipping: "",
        rush: "",
        service:""
      },
      version: "v3",
      statuss: "Active" as "Active" | "Inactive",
    },
    validationSchema: taxSchema,
    validateOnBlur: true,
    validateOnChange: false,
    // context: { mode: taxMode },
    onSubmit: async (values, helpers) => {
      try {
        const isAdd = taxMode === "add" || !taxcloud?.id;
        const url = isAdd ? `${API}/taxcloud/config` : `${API}/taxcloud/config/${taxcloud?.id}`;
        const method = isAdd ? "POST" : "PUT";
        const payload = {
          provider: "taxcloud",
          account_number: "",
          license_key: "",
          client_id: "",
          client_secret: "",
          environment: "prodcution",
          connection_id: values.connection_id,
          api_key: values.api_key,
          origin_address: {
            label: values.origin_address.label,
            addressLine1: values.origin_address.addressLine1,
            addressLine2: values.origin_address.addressLine2,
            city: values.origin_address.city,
            cityCode: values.origin_address.cityCode,
            state: values.origin_address.state,
            stateCode: values.origin_address.stateCode,
            zip: values.origin_address.zip,
            country: values.origin_address.country,
            countryCode: values.origin_address.countryCode,
            phone: values.origin_address.phone,
            // latitude: values.origin_address.latitude,
            // longitude: values.origin_address.longitude,
          },
          tic_codes: {
            default: values.tic_codes.default,
            shipping: values.tic_codes.shipping,
            rush: values.tic_codes.rush,
            service: values.tic_codes.service
          },

          version: values.version,
          status: values.statuss === "Active" ? true : false,
        };
        const res = await fetch(url, {
          method,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to save TaxCloud");
        await res.json().catch(() => ({}));
        showSuccess(isAdd ? "TaxCloud connected!" : "TaxCloud updated!");
        setShowTaxModal(false);
        await fetchIntegrations();
      } catch (e: any) {
        showError(e?.message || "Failed to save TaxCloud.");
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const handleOriginAddressLine1Change = (val: string) => {
    taxForm.setFieldValue("origin_address.addressLine1", val);
    if (!val.trim()) {
      taxForm.setFieldValue("origin_address.city", "", false);
      taxForm.setFieldValue("origin_address.cityCode", "", false);
      taxForm.setFieldValue("origin_address.state", "", false);
      taxForm.setFieldValue("origin_address.stateCode", "", false);
      taxForm.setFieldValue("origin_address.zip", "", false);
      taxForm.setFieldValue("origin_address.country", "", false);
      taxForm.setFieldValue("origin_address.countryCode", "", false);
      taxForm.setFieldValue("origin_address.latitude", undefined, false);
      taxForm.setFieldValue("origin_address.longitude", undefined, false);
    }
  };

  const openTaxModal = async () => {
    if (loading) return; // Prevent opening modal during loading
    if (taxcloud?.id) {
      setTaxMode("edit");
      try {
        const r = await fetch(`${API}/taxcloud/config/${taxcloud.id}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const d = r.ok ? await r.json() : taxcloud;
        taxForm.setValues({
          connection_id: d?.connection_id || "",
          api_key: d?.api_key || "", // keep empty unless user updates
          origin_address: {
            label: d?.origin_address?.label || "",
            addressLine1: d?.origin_address?.addressLine1 || "",
            addressLine2: d?.origin_address?.addressLine2 || "",
            city: d?.origin_address?.city || "",
            state: d?.origin_address?.state || "",
            country: d?.origin_address?.country || "",
            zip: d?.origin_address?.zip || "",
            cityCode: d?.origin_address?.cityCode || "",
            stateCode: d?.origin_address?.stateCode || "",
            countryCode: d?.origin_address?.countryCode || "",
            // latitude: d?.origin_address?.latitude || "",
            // longitude: d?.origin_address?.longitude || "",
            phone: d?.origin_address?.phone || "",
          },
          tic_codes: {
            default: d?.tic_codes?.default || "",
            shipping: d?.tic_codes?.shipping || "",
            rush: d?.tic_codes?.rush || "",
            service: d?.tic_codes?.service || ""
          },
          version: d?.version || "v3",
          statuss: (d?.status === true || d?.status === "Active") ? "Active" : "Inactive",
        });
      } catch {
        const oa = taxcloud.origin_address || ({} as any);
        const tic_codes = taxcloud.tic_codes || ({} as any);
        taxForm.setValues({
          connection_id: taxcloud.connection_id || "",
          api_key: taxcloud?.api_key || "",
          origin_address: {
            label: oa.label || "",
            addressLine1: oa.addressLine1 || "",
            addressLine2: oa.addressLine2 || "",
            city: oa.city || "",
            state: oa.state || "",
            country: oa.country || "",
            zip: oa.zip || "",
            cityCode: oa.cityCode || "",
            stateCode: oa.stateCode || "",
            countryCode: oa.countryCode || "",
            // latitude: undefined,
            // longitude: undefined,
            phone: oa.phone || "",
          },
          tic_codes: {
            default: tic_codes?.default || "",
            shipping: tic_codes?.shipping || "",
            rush: tic_codes?.rush || "",
            service: tic_codes?.service || ""
          },
          version: taxcloud.version || "v3",
          statuss: taxcloud.status || "Active",
        });
      }
    } else {
      setTaxMode("add");
      taxForm.resetForm();
      taxForm.setValues({
        connection_id: "",
        api_key: "",
        origin_address: {
          label: "",
          addressLine1: "",
          addressLine2: "",
          city: "",
          state: "",
          country: "",
          zip: "",
          cityCode: "",
          stateCode: "",
          countryCode: "",
          // latitude: undefined,
          // longitude: undefined,
          phone: "",
        },
        tic_codes: {
          default: "",
          shipping: "",
          rush: "",
          service:""
        },
        version: "v3",
        statuss: "Active",
      });
    }
    setShowTaxModal(true);
  };

  /** ================= Render ================= */
  return (
    <div className="container-fluid bg-gray-50 custom-container">
      <div className="int-wrap container ">
        <div className="int-header">
          <h1 className="fs-22 fw-normal int-title mb-3">Integrations</h1>
          <p className="fs-15 int-subtitle mb-5">Connect your favorite tools and services to streamline your workflow</p>
        </div>

        <div className="int-toolbar mb-5 col-12 col-lg-2  box-shadow-datatables">
          <div className="int-search">
            <i className="bi bi-search int-search-icon" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search integrations..."
            />
          </div>

          <UICustomSelect
            name="category"
            value={category}
            onChange={(e: any) => setCategory(e.target.value as Category)}
            options={["All", "Payment", "Accounting", "Marketing", "Documents", "Automation", "CRM", "Communication"]}
            className="col-12 col-lg-2"   // <- sizing controlled here
          />




          <button
            className={`int-chip ${connectedOnly ? "int-chip-on" : ""} col-12 col-lg-2`}
            onClick={() => setConnectedOnly(v => !v)}
          >
            <i className="bi bi-funnel int-chip-icon"></i>
            <span className="int-chip-text">Connected</span>
          </button>
        </div>


        <h3 className="int-section-heading " style={{ color: "#101828" }}>Popular Integrations</h3>

        <div className="int-grid">
          {CARDS.map((c) => (
            <div key={c.key} className={`int-card ${c.muted ? "int-card-muted" : ""}`}>
              <div className="int-card-head">
                <div className="int-icon">{c.icon}</div>
                {c.connected && (
                  <span className="int-badge int-badge-success">
                    <i className="bi bi-check2"></i>
                    <span>Connected</span>
                  </span>
                )}

              </div>

              <div className="int-card-body py-3">
                <div className="int-card-title fw-normal">{c.name}</div>
                <div className="int-card-desc">
                  {c.id === "stripe"
                    ? "Online payment processing for internet businesses"
                    : c.id === "taxcloud"
                      ? "Automated tax calculations and reporting"
                      : c.id === "ups"
                        ? "Shipping tracking and management"
                        : "—"}
                </div>
                <span className={`int-pill ${pillClass(c.category)} int-pill-pay mb-4 mt-3 p-3`}>{c.category}</span>
              </div>

              <div className="int-card-actions">
                {c.id === "stripe" && (c.connected ? (
                  <button 
                    className="int-btn int-btn-ghost justify-content-center w-100" 
                    onClick={(e) => { if (loading) { e.preventDefault(); return; } openStripeModal(); }} 
                    disabled={loading}
                    style={loading ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                  >
                    <i className="bi bi-gear"></i>
                    <span className="ms-2 int-card-title">Configure</span>
                  </button>

                ) : (
                  <button 
                    className="int-btn int-btn-primary" 
                    onClick={(e) => { if (loading) { e.preventDefault(); return; } openStripeModal(); }} 
                    disabled={loading}
                    style={loading ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                  >
                    ＋ Connect
                  </button>
                ))}
                {c.id === "taxcloud" && (c.connected ? (
                  // <button className="int-btn int-btn-ghost" onClick={openTaxModal}>⚙️ Configure</button>
                  <button 
                    className="int-btn int-btn-ghost justify-content-center w-100" 
                    onClick={(e) => { if (loading) { e.preventDefault(); return; } openTaxModal(); }} 
                    disabled={loading}
                    style={loading ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                  >
                    <i className="bi bi-gear"></i>
                    <span className="ms-2">Configure</span>
                  </button>
                ) : (
                  <button 
                    className="int-btn int-btn-primary" 
                    onClick={(e) => { if (loading) { e.preventDefault(); return; } openTaxModal(); }} 
                    disabled={loading}
                    style={loading ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                  >
                    ＋ Connect
                  </button>
                ))}
                {c.id === "muted" && (
                  <button className="int-btn int-btn-primary" disabled>＋ Connect</button>
                )}
                {c.id === "ups" && (c.connected ? (
                  // If connected (status = true), show Configure button
                  <Link
                    className={`int-btn int-btn-ghost justify-content-center w-100 ${loading ? 'disabled' : ''}`}
                    to={loading ? '#' : `${baseRoute}/ups/settings/${ups?.id}`}
                    onClick={loading ? (e) => e.preventDefault() : undefined}
                    style={loading ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
                  >
                    <i className="bi bi-gear"></i>
                    <span className="ms-2 int-card-title">Configure</span>
                  </Link>
                ) : (
                  // If not connected, check if config exists
                  <Link
                    className={`align-items-center d-flex int-btn int-btn-primary ${loading ? 'disabled' : ''}`}
                    to={loading ? '#' : (ups?.id ? `${baseRoute}/ups/settings/${ups.id}` : `${baseRoute}/ups/settings`)}
                    onClick={loading ? (e) => e.preventDefault() : undefined}
                    style={loading ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
                  >
                    ＋ Connect
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ===== Stripe Modal ===== */}
        {showStripeModal && (
          <Modal
            title={stripeMode === "add" ? "Add Stripe" : "Edit Stripe"}
            onClose={() => setShowStripeModal(false)}
            hideClose
            alignTitle="center"
          >
            {/* <Modal title="Edit Stripe" onClose={() => setShowStripeModal(false)}> */}
            <form onSubmit={stripeForm.handleSubmit}>
              <div className="int-modal-scroll">
                <Field label="Name" required>
                  <UIInput
                    name="name"
                    value={stripeForm.values.name}
                    onChange={stripeForm.handleChange}
                    placeholder="Enter name"
                  />
                  <Err msg={stripeForm.touched.name && stripeForm.errors.name} />
                </Field>

                <Field label="Publishable Key" required>
                  <UIInput
                    name="publishable_key"
                    value={stripeForm.values.publishable_key}
                    onChange={stripeForm.handleChange}
                    placeholder="Enter publishable key"
                  />
                  <Err
                    msg={stripeForm.touched.publishable_key && stripeForm.errors.publishable_key}
                  />
                </Field>

                <Field label="Secret Key" required={stripeMode === "add"}>
                  <UIInput
                    type="password"
                    name="secret_key"
                    value={stripeForm.values.secret_key}
                    onChange={stripeForm.handleChange}
                    placeholder="Enter secret key"
                    autoComplete="new-password"
                  />
                  <Err msg={stripeForm.touched.secret_key && stripeForm.errors.secret_key} />
                </Field>

                <Field label="Status" required>
                  <UISelect
                    name="statuss"
                    value={stripeForm.values.statuss}
                    onChange={stripeForm.handleChange}
                    className="col-auto"
                  >
                    <option>Active</option>
                    <option>Inactive</option>
                  </UISelect>
                  {/* <UICustomSelect
                    name="statuss"
                    value={taxForm.values.statuss}
                    onChange={taxForm.handleChange}
                    options={["Active", "Inactive"]}
                    className="col-12 col-lg-2"
                  /> */}
                  <Err msg={taxForm.touched.statuss && (taxForm.errors.statuss as any)} />

                  <Err msg={stripeForm.touched.statuss && stripeForm.errors.statuss} />
                </Field>
              </div>

              <div className="int-modal-footer">
                {stripe && (
                  <button className="int-btn int-btn-ghost-danger" type="button" onClick={() => setDanger({ for: "stripe" })} disabled={loading}>
                    Disconnect
                  </button>
                )}
                <div className="int-spacer" />
                <button className="int-btn" type="button" onClick={() => setShowStripeModal(false)}>Cancel</button>
                <button className="int-btn int-btn-primary btn-surface fw-bold fs-16" type="submit" disabled={stripeForm.isSubmitting || loading}>
                  {stripe ? "Update" : "Connect"}
                </button>
              </div>
            </form>

            {danger?.for === "stripe" && (
              <DangerInModal title="Disconnect Stripe" onCancel={() => setDanger(null)} onConfirm={disconnectStripe} />
            )}
          </Modal>
        )}

        {/* ===== TaxCloud Modal ===== */}
        {showTaxModal && (
          <Modal
            title={taxMode === "add" ? "Add TaxCloud" : "Edit TaxCloud"}
            onClose={() => setShowTaxModal(false)}
            hideClose
            alignTitle="center"
          >
            {/* <Modal title="Edit TaxCloud" onClose={() => setShowTaxModal(false)}> */}
            <form onSubmit={taxForm.handleSubmit}>
              <div className="int-modal-scroll">

                <Field label="Label" required>
                  <UIInput
                    name="origin_address.label"
                    value={taxForm.values.origin_address.label}
                    onChange={taxForm.handleChange}
                    placeholder="Enter label"
                  />
                  <Err
                    msg={
                      (taxForm.touched.origin_address as any)?.label &&
                      (taxForm.errors.origin_address as any)?.label
                    }
                  />
                </Field>

                <Field label="Connection ID" required>
                  <UIInput
                    name="connection_id"
                    value={taxForm.values.connection_id}
                    onChange={taxForm.handleChange}
                    placeholder="Enter connection ID"
                    autoComplete="off"
                  />
                  <Err msg={taxForm.touched.connection_id && taxForm.errors.connection_id} />
                </Field>

                <Field label="API Key" required>
                  <UIInput
                    type="password"
                    name="api_key"
                    value={taxForm.values.api_key}
                    onChange={taxForm.handleChange}
                    placeholder="Enter API key"
                    autoComplete="new-password"
                  />
                  <Err msg={taxForm.touched.api_key && taxForm.errors.api_key} />
                </Field>

                {/* <h5 style={{ marginTop: 6, marginBottom: 6 }}>Origin Address</h5> */}



                {/* <Field label="Phone" required>
                  <UIInput
                    name="origin_address.phone"
                    value={taxForm.values.origin_address.phone}
                    onChange={taxForm.handleChange}
                    placeholder="Enter phone"
                  />
                  <Err
                    msg={
                      (taxForm.touched.origin_address as any)?.phone &&
                      (taxForm.errors.origin_address as any)?.phone
                    }
                  />
                </Field> */}
                <Field label="Address Line 1" required>
                  <AddressAutocomplete
                    id="origin_address.addressLine1"
                    name="origin_address.addressLine1"
                    value={taxForm.values.origin_address.addressLine1}
                    onChange={handleOriginAddressLine1Change}
                    onSelect={(picked) => {
                      taxForm.setFieldValue("origin_address.addressLine1", picked.addressLine1);
                      taxForm.setFieldValue("origin_address.city", picked.city);
                      taxForm.setFieldValue("origin_address.cityCode", picked.cityCode);
                      taxForm.setFieldValue("origin_address.state", picked.state);
                      taxForm.setFieldValue("origin_address.stateCode", picked.stateCode);
                      taxForm.setFieldValue("origin_address.zip", picked.zipcode);
                      taxForm.setFieldValue("origin_address.country", picked.country);
                      taxForm.setFieldValue("origin_address.countryCode", picked.countryCode);
                      taxForm.setFieldValue("origin_address.latitude", picked.latitude);
                      taxForm.setFieldValue("origin_address.longitude", picked.longitude);
                    }}
                    placeholder="Enter address line 1"
                    className={`form-control ${
                      (taxForm.touched.origin_address as any)?.addressLine1 &&
                      (taxForm.errors.origin_address as any)?.addressLine1
                        ? "is-invalid"
                        : ""
                    }`}
                    error={
                      (taxForm.touched.origin_address as any)?.addressLine1
                        ? ((taxForm.errors.origin_address as any)?.addressLine1 as any)
                        : undefined
                    }
                  />
                  <Err
                    msg={
                      (taxForm.touched.origin_address as any)?.addressLine1 &&
                      (taxForm.errors.origin_address as any)?.addressLine1
                    }
                  />
                </Field>



                <Field label="Address Line 2">
                  <UIInput
                    name="origin_address.addressLine2"
                    value={taxForm.values.origin_address.addressLine2 || ""}
                    onChange={taxForm.handleChange}
                    placeholder="Enter address line 2"
                  />
                </Field>

                <div className="int-row-3">
                  <Field label="City" required>
                    <UIInput
                      name="origin_address.city"
                      value={taxForm.values.origin_address.city}
                      onChange={taxForm.handleChange}
                      placeholder="Enter city"
                      disabled
                    />
                    <Err
                      msg={
                        (taxForm.touched.origin_address as any)?.city &&
                        (taxForm.errors.origin_address as any)?.city
                      }
                    />
                  </Field>

                  <Field label="State" required>
                    <UIInput
                      name="origin_address.state"
                      value={taxForm.values.origin_address.state}
                      onChange={taxForm.handleChange}
                      placeholder="Enter state"
                      disabled
                    />
                    <Err
                      msg={
                        (taxForm.touched.origin_address as any)?.state &&
                        (taxForm.errors.origin_address as any)?.state
                      }
                    />
                  </Field>

                  <Field label="Zip" required>
                    <UIInput
                      name="origin_address.zip"
                      value={taxForm.values.origin_address.zip}
                      onChange={taxForm.handleChange}
                      placeholder="Enter zip"
                      disabled
                    />
                    <Err
                      msg={
                        (taxForm.touched.origin_address as any)?.zip &&
                        (taxForm.errors.origin_address as any)?.zip
                      }
                    />
                  </Field>
                </div>

                <Field label="Country" required>
                  <UIInput
                    name="origin_address.country"
                    value={taxForm.values.origin_address.country}
                    onChange={taxForm.handleChange}
                    placeholder="Enter country"
                    disabled
                  />
                  <Err
                    msg={
                      (taxForm.touched.origin_address as any)?.country &&
                      (taxForm.errors.origin_address as any)?.country
                    }
                  />
                </Field>

                {/* <div className="int-row-3">
    <Field label="Latitude">
      <UIInput
        type="number"
        name="origin_address.latitude"
        value={taxForm.values.origin_address.latitude ?? ""}
        onChange={taxForm.handleChange}
        placeholder="Latitude"
      />
    </Field>
    <Field label="Longitude">
      <UIInput
        type="number"
        name="origin_address.longitude"
        value={taxForm.values.origin_address.longitude ?? ""}
        onChange={taxForm.handleChange}
        placeholder="Longitude"
      />
    </Field>
    <div />
  </div> */}

                <Field label="Version" required>
                  <UIInput
                    name="version"
                    value={taxForm.values.version}
                    onChange={taxForm.handleChange}
                    placeholder="Enter version"
                    disabled
                  />
                  <Err msg={taxForm.touched.version && taxForm.errors.version} />
                </Field>
                <div className="row ms-1" id="row-overlap">
                  <div className="col-md-6 mb-3" id="row-overlap">
                    <Field label="Default TIC Code" required>
                      <UIInput
                        name="tic_codes.default"
                        value={taxForm.values.tic_codes.default}
                        onChange={taxForm.handleChange}
                        placeholder="Enter default TIC code"
                      />
                      <Err
                        msg={
                          (taxForm.touched.tic_codes as any)?.default &&
                          (taxForm.errors.tic_codes as any)?.default
                        }
                      />
                    </Field>
                  </div>

                  <div className="col-md-6 mb-3 " id="row-overlap">
                    <Field label="Shipping TIC Code" required>
                      <UIInput
                        name="tic_codes.shipping"
                        value={taxForm.values.tic_codes.shipping}
                        onChange={taxForm.handleChange}
                        placeholder="Enter shipping TIC code"
                        type="tel"
                      />
                      <Err
                        msg={
                          (taxForm.touched.tic_codes as any)?.shipping &&
                          (taxForm.errors.tic_codes as any)?.shipping
                        }
                      />
                    </Field>
                  </div>
                   <div className="col-md-6 mb-3 " id="row-overlap">
                    <Field label="Special TIC Code" required>
                      <UIInput
                        name="tic_codes.rush"
                        value={taxForm.values.tic_codes.rush}
                        onChange={taxForm.handleChange}
                        placeholder="Enter rush TIC code"
                        type="tel"
                      />
                      <Err
                        msg={
                          (taxForm.touched.tic_codes as any)?.rush &&
                          (taxForm.errors.tic_codes as any)?.rush
                        }
                      />
                    </Field>
                  </div>
                  <div className="col-md-6 mb-3 " id="row-overlap">
                    <Field label="Service TIC Code" required>
                      <UIInput
                        name="tic_codes.service"
                        value={taxForm.values.tic_codes.service}
                        onChange={taxForm.handleChange}
                        placeholder="Enter service TIC code"
                        type="tel"
                      /> 
                      <Err
                        msg={
                          (taxForm.touched.tic_codes as any)?.service &&
                          (taxForm.errors.tic_codes as any)?.service
                        }
                      />
                    </Field>
                  </div>
                </div>


                <Field label="Status" required>
                  <UISelect
                    name="statuss"
                    value={taxForm.values.statuss}
                    onChange={taxForm.handleChange}
                    className=" col-auto"
                  >
                    <option>Active</option>
                    <option>Inactive</option>
                  </UISelect>
                  {/* <UICustomSelect
                    name="statuss"
                    value={taxForm.values.statuss}
                    onChange={taxForm.handleChange}
                    options={["Active", "Inactive"]}
                    className="col-12 col-lg-2"
                  /> */}
                  <Err msg={taxForm.touched.statuss && (taxForm.errors.statuss as any)} />

                  <Err msg={taxForm.touched.statuss && (taxForm.errors.statuss as any)} />
                </Field>

              </div>


              <div className="int-modal-footer mt-3">
                {taxcloud && (
                  <button className="int-btn int-btn-ghost-danger" type="button" onClick={() => setDanger({ for: "taxcloud" })} disabled={loading}>
                    Disconnect
                  </button>
                )}
                <div className="int-spacer" />
                <button className="int-btn" type="button" onClick={() => setShowTaxModal(false)}>Cancel</button>
                <button className="btn save-btn surface-btn fw-bold fs-14" type="submit" disabled={taxForm.isSubmitting || loading}>
                  {taxcloud ? "Update" : "Connect"}
                </button>
              </div>
            </form>
            {danger?.for === "taxcloud" && (
              <DangerInModal title="Disconnect TaxCloud" onCancel={() => setDanger(null)} onConfirm={disconnectTaxcloud} />
            )}
          </Modal>
        )}

        {loading ? <div style={{ marginTop: 12 }}>Loading…</div> : null}
      </div>
    </div>
  );
}

/** ========= Small helpers (no CSS required) ========= */
function Err({ msg }: { msg: any }) {
  if (!msg) return null;
  return <div style={{ color: "#dc2626", fontSize: 12, marginTop: 4 }}>{String(msg)}</div>;
}

function pillClass(cat: Card["category"]) {
  switch (cat) {
    case "Payment": return "int-pill-pay";
    case "Accounting": return "int-pill-acc";
    case "Marketing": return "int-pill-mkt";
    default: return "";
  }
}

function Dropdown({ label, items, onSelect }: { label: string; items: string[]; onSelect: (x: any) => void; }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const f = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", f);
    return () => document.removeEventListener("mousedown", f);
  }, []);
  return (
    <div className="int-dropdown" ref={ref}>
      <button className="int-dropdown-trigger" onClick={() => setOpen(v => !v)}>{label} <span className="int-caret">▾</span></button>
      {open && (
        <div className="int-dropdown-menu">
          {items.map(it => (
            <button key={it} className={`int-dropdown-item ${it === label ? "is-active" : ""}`} onClick={() => { onSelect(it); setOpen(false); }}>
              {it}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
  hideClose = false,
  alignTitle = "start",
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  hideClose?: boolean;
  alignTitle?: "start" | "center" | "end";
}) {
  // map alignment to flex + text styles
  const headerJustify =
    alignTitle === "center" ? "center" : alignTitle === "end" ? "flex-end" : "space-between";
  const titleStyle: React.CSSProperties =
    alignTitle === "center"
      ? { textAlign: "center", width: "100%", fontWeight: 700, fontSize: 16 }
      : { fontWeight: 700, fontSize: 16 };

  return (
    <div className="int-modal-backdrop" onClick={onClose}>
      <div className="int-modal" onClick={(e) => e.stopPropagation()}>
        <div
          className="int-modal-header"
          style={{ display: "flex", alignItems: "center", justifyContent: headerJustify, padding: "16px 18px", borderBottom: "1px solid #eef2f7" }}
        >
          <div className="int-modal-title" style={titleStyle}>{title}</div>
          {!hideClose && (
            <button className="int-modal-x" onClick={onClose} style={{ marginLeft: "auto" }}>
              ✕
            </button>
          )}
        </div>
        <div className="int-modal-body">{children}</div>
      </div>
    </div>
  );
}


// function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode; }) {
//   return (
//     <div className="int-modal-backdrop" onClick={onClose}>
//       <div className="int-modal" onClick={(e) => e.stopPropagation()}>
//         <div className="int-modal-header">
//           <div className="int-modal-title">{title}</div>
//           <button className="int-modal-x" onClick={onClose}>✕</button>
//         </div>
//         <div className="int-modal-body">{children}</div>
//       </div>
//     </div>
//   );
// }


// Make sure once in your app:
// import "bootstrap-icons/font/bootstrap-icons.css";

export function DangerInModal({
  title,
  onCancel,
  onConfirm,
}: {
  title: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="danger-overlay">
      <div className="danger-modal">
        <div className="danger-header">
          <FaExclamationTriangle className="danger-icon" />
          <h3 className="danger-title">{title}</h3>
        </div>
        <p className="danger-msg py-4">
          Are you sure you want to disconnect? This will remove the integration
          and you'll need to reconfigure it to use it again.
        </p>
        <p className="danger-undo">This action cannot be undone.</p>

        <div className="danger-actions">
          <button className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn-danger" onClick={onConfirm}>
            Disconnect
          </button>
        </div>
      </div>
    </div>
  );
}


function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode; }) {
  return (
    <label className="int-field">
      <span className="int-field-label">{label} {required && <span className="int-req">*</span>}</span>
      {children}
    </label>
  );
}
